import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { FeedbackCategoriesResponse } from '../interfaces/feedback.interface';
import { ExecutionOutputResponse } from '../interfaces/execution-output.interface';
import { InsightsRequest, InsightsResponse } from '../interfaces/insights-api.interface';
import { IStreamingMessageV2 } from '../interfaces/streaming-messages.interface';
import { StreamingService } from './streaming.service';
import { StreamingException } from '../exceptions/streaming-exceptions';
// Response shape for the legacy runWorkflow endpoint. Keep flexible with an index signature
// so we don't break when backend returns additional fields; prefer `unknown` over `any`.
@Injectable({
  providedIn: 'root'
})

export class ChatService {
  private eventSource: EventSource | null = null;
  private get baseUrl(): string { return this.configService.apiBaseUrl; }


  // new Version
  private readonly createThreadURL = 'api/v1/workflow/create_thread';
  private readonly getThreadsURL = 'api/v1/workflow/threads';
  private readonly executeCodeURL = 'api/v1/workflow/execute-code';
  private readonly sendFeedbackURL = 'api/v1/workflow/feedback';
  private readonly getFeedbackCategoriesURL = 'api/v1/workflow/feedback/categories';
  private readonly executionResultURL = 'api/v1/workflow/execution-result';

  // version 2 API's
  private readonly startWorkflowURLV2 = 'api/v2/workflow/start';
  private readonly getThreadsByIdURLv2 = 'api/v2/workflow/threads';
  private readonly resumeWorkflowURLV2 = 'api/v2/workflow/resume';
  private readonly stopWorkflowURLV2 = 'api/v2/workflow/stop';
  private readonly executeCodeInsights = 'api/v2/workflow/insights';
  private readonly getInsightsStreamURL = 'api/v2/workflow/insights/stream';
  private readonly getPSSEVersionsURL = 'api/v2/workflow/psse/versions';


  private readonly sharedThreadId = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly streamingService: StreamingService,
    private readonly configService: AppConfigService
  ) { }

  setSharedThreadId(threadId: string): void {
    this.sharedThreadId.next(threadId);
  }

  getSharedThreadId(): Observable<string | null> {
    return this.sharedThreadId.asObservable();
  }



  executeCode(code: string, threadId: string, message_id: string, validateOnly: boolean = false, executionMode: 'electron' | 'browser' = 'browser',isAgentInterrupted: boolean = false): Observable<any> {
    const payload = {
      code: code,
      thread_id: threadId,
      message_id: message_id,
      validate_only: validateOnly,
      execution_mode: executionMode,
      is_agent_interrupted: isAgentInterrupted
    };
    return this.http.post<any>(`${this.baseUrl}/${this.executeCodeURL}`, payload);
  }

  async executeRun(threadId: string, code: string, pythonVersion: string): Promise<string> {
    // Call Electron IPC to execute code in local PSSE (standalone Run button).
    // SECURITY (Issues 3 & 4): Hash is computed at the moment of the user's action
    // (Execute button click or stream approval click). This binds the user's consent
    // to the exact code they reviewed. Electron re-verifies before any execution.
    if (typeof (window as any).psse !== 'undefined') {
      const codeHash = await this.computeCodeHash(code);
      return (window as any).psse.executePSSECode({ code, pythonVersion, codeHash });
    }
    return Promise.reject('Electron not available');
  }

  private async computeCodeHash(code: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(code);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  executeInsights(code: string, threadId: string, messageId: string, executionResult: string): Observable<InsightsResponse> {
    const payload: InsightsRequest = {
      code,
      thread_id: threadId,
      message_id: messageId,
      execution_result: executionResult
    };
    return this.http.post<InsightsResponse>(`${this.baseUrl}/${this.executeCodeInsights}`, payload);
  }

  connectInsightsStream(
    threadId: string,
    messageId: string,
    onMessage: (event: IStreamingMessageV2) => void,
    onError?: (error: StreamingException) => void
  ): void {
    const streamUrl = `${this.baseUrl}/${this.getInsightsStreamURL}/${threadId}/${messageId}`;
    this.streamingService.connect<IStreamingMessageV2>(streamUrl, onMessage, onError);
  }

  submitRunExecutionResult(threadId: string, executionResult: string): Observable<ExecutionOutputResponse> {
    const payload = {
      thread_id: threadId,
      execution_result: executionResult
    };
    return this.http.post<ExecutionOutputResponse>(`${this.baseUrl}/${this.executionResultURL}`, payload);
  }

  sendFeedback(messageId: string, feedback: string, threadId: string, feedbackMessage?: string, feedbackCategory?: string) {
    const payload = {
      "message_id": messageId,
      "feedback": feedback,
      "thread_id": threadId,
      "feedback_message": feedbackMessage ?? '',
      "feedback_category": feedbackCategory ?? ''
    };
    return this.http.post(`${this.baseUrl}/${this.sendFeedbackURL}`, payload);
  }

  getFeedbackCategories(): Observable<FeedbackCategoriesResponse> {
    // Fetch categories from backend API
    return this.http.get<FeedbackCategoriesResponse>(`${this.baseUrl}/${this.getFeedbackCategoriesURL}`);
  }

  // File attachment methods
  attachFile(filePath: string, threadId: string, originalFilename?: string, storedFilename?: string, fileSize?: number): Observable<any> {
    const payload: any = {
      file_path: filePath,
      thread_id: threadId
    };
    if (originalFilename) {
      payload.original_filename = originalFilename;
    }
    if (storedFilename) {
      payload.stored_filename = storedFilename;
    }
    if (fileSize != null) {
      payload.file_size = fileSize;
    }
    return this.http.post<any>(`${this.baseUrl}/api/v2/file/attach-file`, payload);
  }

  getThreadFiles(threadId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/v2/file/thread/${threadId}/files`);
  }

  deleteFile(attachmentId: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/v2/file/${attachmentId}`);
  }


  // new RAG version API's

  createThread(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${this.createThreadURL}`);
  }


  startWorkflow(message: string, threadId: string, psseVersion?: string): Observable<any> {
    const payload: any = {
      "question": message
    };
    if (psseVersion) {
      payload.psse_version = psseVersion;
    }
    return this.http.post<any>(`${this.baseUrl}/${this.startWorkflowURLV2}/${threadId}`, payload);
  }


  getThreadsList(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${this.getThreadsURL}`);
  }

  getThreadById(threadId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${this.getThreadsByIdURLv2}/${threadId}/messages`);
  }

  submitExecutionResult(threadId: string, messageId: string, executionResult: string): Observable<any> {
    const payload = {
      "thread_id": threadId,
      "message_id": messageId,
      "interaction_type": "code_execution",
      "data": {
        "output": executionResult
      }

    };
    return this.http.post<any>(`${this.baseUrl}/${this.resumeWorkflowURLV2}`, payload);
  }

  resumeWorkflowV2(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${this.resumeWorkflowURLV2}`, payload);
  }

  stopWorkflow(threadId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${this.stopWorkflowURLV2}`, { thread_id: threadId });
  }

  // PSSE Version Management
  getPSSEVersions(): Observable<{ default_version: string; versions: string[] }> {
    return this.http.get<{ default_version: string; versions: string[] }>(`${this.baseUrl}/${this.getPSSEVersionsURL}`);
  }


  closeSSE(): void {
    if (this.eventSource) {
      this.eventSource.close();
    }
  }
}
